import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Archive,
  Search,
  Users,
  Calendar,
  Clock,
  MapPin,
  Briefcase,
  ArchiveRestore,
  UserPlus,
  ChevronDown,
  ChevronUp,
  X as XIcon,
  BedDouble,
  Bus,
  Tent,
  Shirt,
  Banknote,
  FileText,
  Navigation,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { saveAs } from 'file-saver';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArchivedEventsPage() {
  const [openShifts, setOpenShifts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  // Per-card expanded state: { [groupKey]: true|false } → show staff list
  const [expanded, setExpanded] = useState({});
  // Add-Extra-Staff dialog state
  const [extraDialog, setExtraDialog] = useState({ open: false, group: null });
  const [extraSearch, setExtraSearch] = useState('');
  const [extraUserId, setExtraUserId] = useState(null);
  const [extraDates, setExtraDates] = useState([]);
  // Per-event-day hours summary (scheduled vs worked) keyed by open_shift id.
  const [hoursSummary, setHoursSummary] = useState({});

  const fetchArchived = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/open-shifts?archived=true`);
      setOpenShifts(res.data || []);
      axios.get(`${API}/admin/events/hours-summary`)
        .then((r) => setHoursSummary(r.data?.summaries || {}))
        .catch(() => {});
    } catch (_e) {
      toast.error('Failed to load archived events');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/clock/all-employees`);
      setAllUsers(res.data?.employees || []);
    } catch (_e) {
      // best-effort
    }
  }, []);

  useEffect(() => {
    fetchArchived();
    fetchUsers();
  }, [fetchArchived, fetchUsers]);

  // Group archived open_shifts using same logic as Upcoming Shifts —
  // position + location ONLY (normalised). Times are deliberately ignored so
  // an event whose days have different (per-day) times still shows as ONE
  // card and gets restored as a whole.
  const grouped = useMemo(() => {
    const norm = (v) => (v || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
    const map = {};
    openShifts.forEach((s) => {
      const key = `${norm(s.position)}|${norm(s.location)}`;
      if (!map[key]) {
        map[key] = {
          key,
          position: s.position,
          location: s.location,
          shifts: [],
          archived_at: s.archived_at,
        };
      }
      map[key].shifts.push(s);
      if (s.archived_at && (!map[key].archived_at || s.archived_at > map[key].archived_at)) {
        map[key].archived_at = s.archived_at;
      }
    });
    return Object.values(map).map((g) => {
      const times = [...new Set(g.shifts
        .filter((s) => s.start_time && s.end_time)
        .map((s) => `${s.start_time} – ${s.end_time}`))];
      // Event-level details: take the first non-empty value across the days.
      const pick = (field) => {
        for (const s of g.shifts) {
          const v = s[field];
          if (v !== undefined && v !== null && v !== '' && v !== false) return v;
        }
        return null;
      };
      return {
        ...g,
        shifts: g.shifts.sort((a, b) => (a.date || '').localeCompare(b.date || '')),
        time_label: times.length === 1 ? times[0] : (times.length > 1 ? 'Varies by day' : ''),
        info: {
          address: pick('address'),
          postcode: pick('postcode'),
          dress_code: pick('dress_code'),
          notes: pick('notes'),
          accommodation_provided: pick('accommodation_provided'),
          accommodation_cost_per_day: pick('accommodation_cost_per_day'),
          accommodation_notes: pick('accommodation_notes'),
          camping_provided: pick('camping_provided'),
          transport_provided: pick('transport_provided'),
          transport_location: pick('transport_location'),
          transport_departure_time: pick('transport_departure_time'),
          transport_notes: pick('transport_notes'),
          hourly_payment: pick('hourly_payment'),
          hourly_rate_breakdown: pick('hourly_rate_breakdown'),
          currency: pick('currency'),
        },
      };
    });
  }, [openShifts]);

  const filtered = grouped.filter((g) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      (g.location || '').toLowerCase().includes(q) ||
      (g.position || '').toLowerCase().includes(q)
    );
  });

  const [pdfBusy, setPdfBusy] = useState({});
  const handleEventPdf = async (group) => {
    setPdfBusy((p) => ({ ...p, [group.key]: true }));
    try {
      const ids = group.shifts.map((s) => s.id).join(',');
      const res = await axios.get(`${API}/admin/events/event-pdf`, {
        params: { day_ids: ids },
        responseType: 'blob',
      });
      const safe = `${group.position || 'Event'}_${group.location || ''}`
        .replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_').slice(0, 60);
      saveAs(new Blob([res.data], { type: 'application/pdf' }), `Event_${safe}.pdf`);
      toast.success('Event PDF downloaded', {
        description: "Saved to your Downloads folder — open the download notification to view it.",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate event PDF');
    } finally {
      setPdfBusy((p) => ({ ...p, [group.key]: false }));
    }
  };

  const handleUnarchive = async (group) => {
    if (!window.confirm(`Restore "${group.location}" to Upcoming Shifts? It will become editable again.`)) return;
    try {
      const ids = group.shifts.map((s) => s.id);
      const res = await axios.post(`${API}/open-shifts/unarchive-group`, { shift_ids: ids });
      toast.success(res.data?.message || 'Event restored');
      fetchArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
  };

  // Aggregate distinct claimed staff across all days of an event.
  // Map: user_id → { user_id, user_name, user_role, days: [date,...] }
  const getClaimedStaff = (group) => {
    const map = {};
    group.shifts.forEach((s) => {
      (s.claimed_by_list || []).forEach((c) => {
        if (!map[c.user_id]) {
          map[c.user_id] = { ...c, days: [] };
        }
        if (!map[c.user_id].days.includes(s.date)) {
          map[c.user_id].days.push(s.date);
        }
      });
    });
    return Object.values(map).sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''));
  };

  const handleAddExtra = async () => {
    if (!extraUserId) { toast.error('Pick a staff member'); return; }
    if (extraDates.length === 0) { toast.error('Pick at least one day'); return; }
    const group = extraDialog.group;
    const shiftIds = group.shifts.filter((s) => extraDates.includes(s.date)).map((s) => s.id);
    try {
      const notify = window.confirm('Send email notification to the affected staff?\n\nOK = send email now\nCancel = save WITHOUT sending any email');
      const res = await axios.post(`${API}/open-shifts/bulk-add-extra-staff?notify=${notify}`, { user_id: extraUserId, shift_ids: shiftIds });
      const { added = 0, skipped = 0 } = res.data || {};
      if (added > 0) {
        toast.success(`Added to ${added} day${added !== 1 ? 's' : ''}${skipped ? ` (${skipped} skipped)` : ''}`);
      } else {
        toast.info(res.data?.message || 'Could not add staff');
      }
      setExtraUserId(null);
      setExtraDates([]);
      setExtraSearch('');
      setExtraDialog({ open: false, group: null });
      fetchArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    }
  };

  const openExtraDialog = (group) => {
    setExtraUserId(null);
    setExtraDates(group.shifts.map((s) => s.date));
    setExtraSearch('');
    setExtraDialog({ open: true, group });
  };

  const filteredUsers = allUsers.filter((u) => {
    if (u.role === 'admin') return false;
    if (!extraSearch) return true;
    const q = extraSearch.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <Archive className="w-6 h-6 text-primary" strokeWidth={1.5} />
              ARCHIVED EVENTS
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Past events — view claimed staff, add extra, or restore back to Upcoming Shifts.
            </p>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by location or position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-background border-border rounded-sm"
            data-testid="archive-search-input"
          />
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground p-4">Loading…</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center space-y-2">
              <Archive className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'No archived events match your search.' : 'No archived events yet.'}
              </p>
              <p className="text-xs text-muted-foreground/70">
                Archive an event from Upcoming Shifts to see it here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((group) => {
              const claimed = getClaimedStaff(group);
              const isExpanded = !!expanded[group.key];
              return (
                <Card
                  key={group.key}
                  className="bg-card border-slate-500/30 rounded-sm"
                  data-testid={`archived-event-${group.key}`}
                >
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1 min-w-0">
                        <Badge className="text-[9px] uppercase tracking-wider bg-slate-500/20 text-slate-300 border-slate-500/40">
                          <Archive className="w-3 h-3 mr-1" />
                          Archived
                        </Badge>
                        <h3 className="text-base font-barlow tracking-wider font-semibold flex items-center gap-2 break-words">
                          <Briefcase className="w-4 h-4 shrink-0 text-muted-foreground" />
                          {group.position}
                        </h3>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                      <span className="break-words">{group.location || '—'}</span>
                    </div>

                    {group.time_label && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4 shrink-0" />
                        <span className="font-mono">{group.time_label}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span>
                        {group.shifts.length} day{group.shifts.length !== 1 ? 's' : ''} •{' '}
                        {(() => {
                          try {
                            const first = format(parseISO(group.shifts[0].date), 'MMM d');
                            const last = format(parseISO(group.shifts[group.shifts.length - 1].date), 'MMM d, yyyy');
                            return first === last.split(',')[0] ? last : `${first} – ${last}`;
                          } catch {
                            return '';
                          }
                        })()}
                      </span>
                    </div>

                    {/* Full event details */}
                    {(() => {
                      const inf = group.info || {};
                      const cur = { GBP: '£', EUR: '€', USD: '$' }[inf.currency] || (inf.currency ? `${inf.currency} ` : '£');
                      const rows = [];
                      if (inf.address || inf.postcode) {
                        rows.push(['addr', Navigation, [inf.address, inf.postcode].filter(Boolean).join(', ')]);
                      }
                      if (inf.hourly_rate_breakdown && Object.keys(inf.hourly_rate_breakdown).length) {
                        rows.push(['pay', Banknote, Object.entries(inf.hourly_rate_breakdown)
                          .filter(([, v]) => v)
                          .map(([k, v]) => `${k}: ${cur}${v}/h`).join(' · ')]);
                      } else if (inf.hourly_payment) {
                        rows.push(['pay', Banknote, `${cur}${inf.hourly_payment}/h`]);
                      }
                      if (inf.accommodation_provided) {
                        rows.push(['accom', BedDouble, `Accommodation provided${inf.accommodation_cost_per_day ? ` — ${cur}${inf.accommodation_cost_per_day}/day` : ''}${inf.accommodation_notes ? ` · ${inf.accommodation_notes}` : ''}`]);
                      } else if (inf.accommodation_notes) {
                        rows.push(['accom', BedDouble, inf.accommodation_notes]);
                      }
                      if (inf.camping_provided) rows.push(['camp', Tent, 'Camping provided']);
                      if (inf.transport_provided || inf.transport_location || inf.transport_notes) {
                        const t = [
                          inf.transport_provided ? 'Transport provided' : null,
                          inf.transport_location ? `pickup: ${inf.transport_location}` : null,
                          inf.transport_departure_time ? `departure ${inf.transport_departure_time}` : null,
                          inf.transport_notes || null,
                        ].filter(Boolean).join(' · ');
                        rows.push(['trans', Bus, t]);
                      }
                      if (inf.dress_code) rows.push(['dress', Shirt, `Dress code: ${inf.dress_code}`]);
                      if (inf.notes) rows.push(['notes', FileText, inf.notes]);
                      if (!rows.length) return null;
                      return (
                        <div className="space-y-1.5 border-t border-border/60 pt-2" data-testid={`event-info-${group.key}`}>
                          {rows.map(([key, Icon, text]) => (
                            <div key={key} className="flex items-start gap-2 text-xs text-muted-foreground">
                              <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary/70" />
                              <span className="break-words whitespace-pre-wrap">{text}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {(() => {
                      const tot = group.shifts.reduce((acc, s) => {
                        const h = hoursSummary[s.id];
                        if (h) {
                          acc.sch += h.scheduled_hours || 0;
                          acc.wrk += h.worked_hours || 0;
                          acc.pend += h.pending_times || 0;
                        }
                        return acc;
                      }, { sch: 0, wrk: 0, pend: 0 });
                      if (!tot.sch && !tot.wrk && !tot.pend) return null;
                      const fmt = (n) => Number(n.toFixed(2));
                      return (
                        <div
                          className="flex flex-wrap items-center gap-2"
                          data-testid={`hours-summary-${group.key}`}
                        >
                          <Badge className="border bg-sky-500/15 text-sky-300 border-sky-500/40 text-[11px]">
                            Scheduled: {fmt(tot.sch)} h
                          </Badge>
                          <Badge className="border bg-green-500/15 text-green-400 border-green-500/40 text-[11px]">
                            Worked: {fmt(tot.wrk)} h
                          </Badge>
                          {tot.pend > 0 && (
                            <span className="text-[10px] text-amber-400/80 italic" title="Shifts without confirmed times are not counted in Scheduled">
                              +{tot.pend} no times set
                            </span>
                          )}
                        </div>
                      );
                    })()}

                    {/* View Staff toggle */}
                    <button
                      type="button"
                      onClick={() => setExpanded((p) => ({ ...p, [group.key]: !p[group.key] }))}
                      className="w-full flex items-center justify-between text-sm text-foreground hover:text-primary px-2 py-1.5 bg-muted/30 border border-border rounded-sm transition-colors"
                      data-testid={`view-staff-${group.key}`}
                    >
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {claimed.length} staff assigned
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {isExpanded && (
                      <div className="space-y-1 max-h-56 overflow-y-auto px-1 pb-1" data-testid={`staff-list-${group.key}`}>
                        {claimed.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic px-2 py-3 text-center">No staff claimed this event.</p>
                        ) : (
                          claimed.map((c) => (
                            <div key={c.user_id} className="flex items-center justify-between gap-2 text-xs bg-background border border-border rounded-sm px-2 py-1.5">
                              <div className="min-w-0">
                                <p className="font-medium text-foreground truncate">{c.user_name}</p>
                                <p className="text-muted-foreground text-[10px]">
                                  {c.user_role === 'supervisor' ? 'Supervisor' : 'Employee'} • {c.days.length} day{c.days.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                              {c.is_extra && (
                                <Badge className="text-[9px] uppercase bg-purple-500/20 text-purple-300 border-purple-500/40">
                                  Extra
                                </Badge>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {group.archived_at && (
                      <p className="text-[10px] text-muted-foreground/70">
                        Archived on {(() => { try { return format(parseISO(group.archived_at), 'MMM d, yyyy'); } catch { return ''; } })()}
                      </p>
                    )}

                    <div className="pt-2">
                      <Button
                        variant="outline"
                        onClick={() => handleEventPdf(group)}
                        disabled={!!pdfBusy[group.key]}
                        className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm text-xs"
                        data-testid={`event-pdf-${group.key}`}
                      >
                        <FileText className="w-4 h-4 mr-1.5" />
                        {pdfBusy[group.key] ? 'Generating…' : 'Download Event PDF'}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => openExtraDialog(group)}
                        className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 rounded-sm text-xs"
                        data-testid={`add-extra-${group.key}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1.5" />
                        Add Extra
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleUnarchive(group)}
                        className="border-primary/40 text-primary hover:bg-primary/10 rounded-sm text-xs"
                        data-testid={`unarchive-${group.key}`}
                      >
                        <ArchiveRestore className="w-4 h-4 mr-1.5" />
                        Restore
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Extra Staff Dialog */}
      <Dialog
        open={extraDialog.open}
        onOpenChange={(o) => {
          if (!o) {
            setExtraDialog({ open: false, group: null });
            setExtraUserId(null);
            setExtraDates([]);
            setExtraSearch('');
          }
        }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Add Extra Staff
            </DialogTitle>
          </DialogHeader>
          {extraDialog.group && (
            <div className="space-y-4 mt-2">
              <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-sm">
                <strong>{extraDialog.group.position}</strong> @ {extraDialog.group.location}
              </div>

              {/* Day selection */}
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Days</p>
                <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
                  {extraDialog.group.shifts.map((s) => {
                    const picked = extraDates.includes(s.date);
                    return (
                      <label key={s.id} className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded-sm border cursor-pointer text-xs',
                        picked ? 'bg-primary/15 border-primary/40' : 'bg-background border-border hover:border-primary/30'
                      )}>
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => setExtraDates((prev) => picked ? prev.filter((d) => d !== s.date) : [...prev, s.date])}
                          className="w-3.5 h-3.5 accent-primary"
                        />
                        <span>{(() => { try { return format(parseISO(s.date), 'EEE, MMM d'); } catch { return s.date; } })()}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Staff picker */}
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Staff Member</p>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={extraSearch}
                    onChange={(e) => setExtraSearch(e.target.value)}
                    className="pl-7 h-8 text-sm bg-background border-border rounded-sm"
                  />
                </div>
                <div className="max-h-44 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                  {filteredUsers.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-3 text-center">No staff found</p>
                  ) : (
                    filteredUsers.slice(0, 20).map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setExtraUserId(u.id)}
                        className={cn(
                          'w-full text-left px-2 py-1.5 text-xs flex items-center justify-between hover:bg-primary/5',
                          extraUserId === u.id && 'bg-primary/15'
                        )}
                      >
                        <span>
                          <span className="font-medium">{u.name}</span>{' '}
                          <span className="text-muted-foreground">({u.role})</span>
                        </span>
                        {extraUserId === u.id && <Badge className="text-[9px]">Selected</Badge>}
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => setExtraDialog({ open: false, group: null })}
                  className="border-border rounded-sm"
                >
                  <XIcon className="w-4 h-4 mr-1.5" />
                  Cancel
                </Button>
                <Button
                  onClick={handleAddExtra}
                  disabled={!extraUserId || extraDates.length === 0}
                  data-testid="confirm-add-extra-archive"
                  className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  Add
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
